import axios from "axios";
import { isTokenExpired } from "../../utils/tokenUtils";
import { getCurrentUser, removeUserFromStorage } from "../../utils/userStorage";
import { isBillingPaymentUrl } from "../../utils/subscriptionAccess";
import { refreshAccessToken } from "../../utils/tokenRefresh";
import showErrorToast from "../../components/CustomComponents/toast/toast";

const SUBSCRIPTION_RESTRICTED_MESSAGE =
  "Subscription grace period has ended. Pay your invoice in Billing to restore full access.";

const isMutatingMethod = (method) => {
  const m = (method || "get").toLowerCase();
  return m === "post" || m === "put" || m === "patch" || m === "delete";
};

let API_URL =
  process.env.REACT_APP_Environment === "development"
    ? process.env.REACT_APP_API_URL
    : process.env.REACT_APP_API_URL_Production;

const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 seconds timeout
  validateStatus: function (status) {
    // Treat 401 as an error so the refresh interceptor can renew the JWT
    return status < 500 && status !== 401;
  }
});

const forceLogout = () => {
  removeUserFromStorage();
  localStorage.removeItem("keepMeLogin");
  window.dispatchEvent(new CustomEvent("forceLogout"));
};

const isAuthBootstrapRequest = (config) => {
  const url = (config?.url || "").toLowerCase();
  return (
    url.includes("loginbackoffice") ||
    url.includes("refreshtoken") ||
    url.includes("consumeposhandofftoken")
  );
};

// Request interceptor to add auth token (refresh first if expired)
axiosInstance.interceptors.request.use(async (config) => {
  // Login / refresh must not depend on (or clear) an existing session
  if (isAuthBootstrapRequest(config)) {
    return config;
  }

  const user = getCurrentUser();

  if (user?.subscriptionRestricted && isMutatingMethod(config.method)) {
    const requestPath = config.url || "";
    if (!isBillingPaymentUrl(requestPath)) {
      showErrorToast(SUBSCRIPTION_RESTRICTED_MESSAGE);
      return Promise.reject(new axios.CanceledError(SUBSCRIPTION_RESTRICTED_MESSAGE));
    }
  }

  if (user?.token) {
    let token = user.token;

    if (isTokenExpired(token)) {
      try {
        const refreshed = await refreshAccessToken();
        token = refreshed.token;
      } catch (refreshError) {
        console.warn(
          "Token refresh before request failed:",
          refreshError?.message || refreshError
        );
        forceLogout();
        return Promise.reject(refreshError);
      }
    }

    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor to handle 401 errors and token refresh
axiosInstance.interceptors.response.use(
  (response) => {
    if (
      response.status === 403 &&
      typeof response.data?.message === "string" &&
      response.data.message.includes("Subscription grace period")
    ) {
      showErrorToast(response.data.message);
      return Promise.reject(new axios.CanceledError(response.data.message));
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized — renew JWT and retry (for any active BackOffice session)
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const user = getCurrentUser();
        if (!user?.token) {
          forceLogout();
          return Promise.reject(error);
        }

        const refreshed = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${refreshed.token}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.warn(
          "Token refresh after 401 failed:",
          refreshError?.response?.data?.message || refreshError?.message || refreshError
        );
        forceLogout();
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
